import { foldSearchText } from './strings';

/**
 * The platform's option matcher for searchable Selects.
 *
 * Why this exists at all: many of our option labels are ReactNodes — the card
 * pickers render an icon tile plus an indented name, the data-type picker
 * renders an icon plus a word. antd's default filter stringifies whatever
 * `optionFilterProp` names (`value` by default), so those selects searched
 * against a hex ObjectId and matched nothing: typing a card name produced "No
 * data". Rather than teach every call site a bespoke filterOption, options
 * carry a plain `searchText` and this one function reads it.
 *
 * Match order:
 *   1. `option.searchText` — the plain-text twin of a ReactNode label.
 *   2. `option.label`, but only when it is a plain string/number.
 *   3. `option.descendantSearchTexts` — a card option shows when a card below
 *      it matches, so a found card keeps its parents in a tree picker.
 *   4. Nothing. An option with none of these never matches, which is honest:
 *      we cannot guess what a React element says.
 *
 * Group headers ({label, options: [...]}) are passed through by rc-select too,
 * and are never matched — a header hit would dump every child into the results.
 *
 * Both sides are folded before comparing (`foldSearchText`, utils/strings.js):
 * lower case, compatibility forms (NFKD — "m³" reads "m3") and diacritics
 * stripped, so "cafe" finds "Café" and "m³" is found by "m3". Every searchable
 * Select in the product answers a keystroke the same way, and the builders'
 * value check (utils/idValueValidation.js) folds typed values with it too.
 */
export function optionSearchFilter ( input, option ) {
	const needle = foldSearchText( input ).trim();
	if ( ! needle ) return true;
	if ( Array.isArray( option?.options ) ) return false;

	const searchText = option?.searchText;
	const label = option?.label;
	const haystack = ( typeof searchText === 'string' && searchText )
		|| ( typeof searchText === 'number' ? String( searchText ) : '' )
		|| ( typeof label === 'string' || typeof label === 'number' ? String( label ) : '' );
	if ( haystack && foldSearchText( haystack ).includes( needle ) ) return true;

	// A card option also shows when a card below it matches, so a found card
	// keeps its parents above it (withDescendantSearchTexts, components/cards/CardVisuals.js).
	const below = option?.descendantSearchTexts;
	return Array.isArray( below ) && below.some( text => foldSearchText( text ).includes( needle ) );
}
